/**
 * SWMCognaraPlanner — full 4-phase Cognara planning drawer for a Structural Tag.
 *
 *   Phase 1  Link tag → Macro schedule activity   (existing button opens this at tab='link')
 *   Phase 2  Cognara sequences marks              (tab='sequence' — SUGGESTED plan)
 *   Phase 3  Human approves / edits / rejects     (tab='approve')
 *   Phase 4  Cognara daily manpower + equipment   (tab='resources')
 *
 * The wizard reads the tag's `cognaraStatus` on mount to jump to the right tab.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2, X, Check, Sparkles, CalendarCheck, Users, Link2, Search,
  ChevronRight, XCircle, AlertTriangle, Wrench, HardHat,
} from 'lucide-react';

const TABS = [
  { id: 'link',      label: '1 · Link Activity',    icon: Link2 },
  { id: 'sequence',  label: '2 · Cognara Sequence', icon: Sparkles },
  { id: 'approve',   label: '3 · Approve Plan',     icon: CalendarCheck },
  { id: 'resources', label: '4 · Resources',        icon: Users },
];

export default function SWMCognaraPlanner({ projectId, structuralTag, onClose, onSaved }) {
  const [tab, setTab] = useState('link');
  const [tagDoc, setTagDoc] = useState(null);
  const [marks, setMarks] = useState([]);
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Phase 1 state
  const [activities, setActivities] = useState([]);
  const [activityQ, setActivityQ] = useState('');
  const [pickedActivity, setPickedActivity] = useState(null);
  const [priorityOverride, setPriorityOverride] = useState('');

  // Phase 3 state — decisions map { markNumber: {decision, edits} }
  const [decisions, setDecisions] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(structuralTag)}/plan`);
      setTagDoc(data.tag); setMarks(data.marks || []); setResources(data.resources);
      // Auto-jump to the most-relevant tab based on state.
      const status = data.tag?.cognaraStatus;
      if (!data.tag?.linkedMacroActivityId) setTab('link');
      else if (status === 'SUGGESTED') setTab('approve');
      else if (status === 'APPROVED' || status === 'PARTIALLY_APPROVED') setTab('resources');
      else setTab('sequence');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load plan'); }
    finally { setLoading(false); }
  }, [projectId, structuralTag]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (tab !== 'link' || activities.length) return;
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/swm/cognara/macro-activities`);
        setActivities(data.activities || []);
        if (data.activities?.length === 0 && data.note) toast.info(data.note);
      } catch (e) { toast.error('Failed to load macro activities'); }
    })();
  }, [tab, activities.length, projectId]);

  const filteredActivities = useMemo(() => {
    const q = activityQ.trim().toLowerCase();
    if (!q) return activities.slice(0, 500);
    return activities.filter(a =>
      (a.wbs || '').toLowerCase().includes(q) ||
      (a.name || '').toLowerCase().includes(q)
    ).slice(0, 500);
  }, [activities, activityQ]);

  // ── Phase 1 · Link ──
  const doLink = async () => {
    if (!pickedActivity) { toast.error('Pick an activity first'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/cognara/link-activity`, {
        structuralTags: [structuralTag],
        macroActivityId: pickedActivity.id,
        priority: priorityOverride ? Number(priorityOverride) : null,
      });
      toast.success(`Linked · P${data.priority} inherited · ${data.linkedActivity.name}`);
      await refresh();
      setTab('sequence');
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
    finally { setBusy(false); }
  };

  // ── Phase 2 · Cognara sequence ──
  const runSequence = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(structuralTag)}/sequence`);
      toast.success(`Cognara sequenced ${data.proposedCount} marks · review below`);
      await refresh();
      setTab('approve');
    } catch (e) { toast.error(e.response?.data?.detail || 'Cognara sequence failed'); }
    finally { setBusy(false); }
  };

  // ── Phase 3 · Approve ──
  const setDecision = (mno, decision, edits) => {
    setDecisions(prev => ({ ...prev, [mno]: { decision, edits: edits ?? prev[mno]?.edits } }));
  };
  const setEdit = (mno, field, value) => {
    setDecisions(prev => ({
      ...prev,
      [mno]: { decision: prev[mno]?.decision || 'APPROVE', edits: { ...(prev[mno]?.edits || {}), [field]: value } },
    }));
  };
  const approveAll = () => {
    const next = {};
    marks.filter(m => m.cognaraStatus === 'SUGGESTED').forEach(m => { next[m.markNumber] = { decision: 'APPROVE' }; });
    setDecisions(next);
  };
  const submitApprovals = async () => {
    const entries = Object.entries(decisions);
    if (!entries.length) { toast.error('Approve or reject at least one mark'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(structuralTag)}/approve-plan`, {
        decisions: entries.map(([mno, d]) => ({ markNumber: mno, decision: d.decision, edits: d.edits })),
      });
      toast.success(`${data.approved} approved · ${data.rejected} rejected · tag now ${data.tagStatus}`);
      setDecisions({});
      await refresh();
      setTab('resources');
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
    finally { setBusy(false); }
  };

  // ── Phase 4 · Resources ──
  const runResources = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(structuralTag)}/resources`);
      toast.success('Cognara resource plan ready · review daily loading below');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cognara resources failed'); }
    finally { setBusy(false); }
  };
  const approveResources = async () => {
    if (!resources?.id) return;
    setBusy(true);
    try {
      await api.post(`/v1/projects/${projectId}/swm/cognara/tags/${encodeURIComponent(structuralTag)}/approve-resources`, {
        resourcePlanId: resources.id, decision: 'APPROVE',
      });
      toast.success('Resource plan approved');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };

  const linkedActivity = tagDoc?.linkedMacroActivityName;
  const suggestedCount = marks.filter(m => m.cognaraStatus === 'SUGGESTED').length;
  const approvedCount = marks.filter(m => m.cognaraStatus === 'APPROVED').length;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" style={{ background: 'rgba(10,14,26,0.65)' }} data-testid="swm-cognara-planner">
      <div className="w-full max-w-[1100px] h-full flex flex-col shadow-2xl" style={{ background: 'var(--card-bg)' }}>
        <div className="flex items-center justify-between border-b p-4" style={{ borderColor: 'var(--border-color)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-mono" style={{ color: '#F26B1F' }}>Cognara Planner</div>
            <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{structuralTag}</div>
            <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {tagDoc?.area || 'Unassigned'} · {marks.length} marks · {linkedActivity ? `Linked to ${linkedActivity}` : 'Not yet linked'}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-black/10" data-testid="swm-cognara-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex border-b" style={{ borderColor: 'var(--border-color)' }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`swm-cognara-tab-${t.id}`}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-[0.12em] transition"
                style={{ background: active ? 'rgba(242,107,31,0.10)' : 'transparent',
                         color: active ? '#F26B1F' : 'var(--text-muted)',
                         borderBottom: active ? '2px solid #F26B1F' : '2px solid transparent' }}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#F26B1F' }} />
            </div>
          ) : tab === 'link' ? (
            <div className="space-y-3">
              <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Pick the macro schedule activity that governs the fabrication + erection window for <b>{structuralTag}</b>.</div>
              {linkedActivity && (
                <div className="p-2.5 rounded ring-1 flex items-center gap-2" style={{ background: 'rgba(14,124,107,0.08)', borderColor: 'rgba(14,124,107,0.4)', color: 'var(--text-primary)' }}>
                  <Check className="w-4 h-4 text-verdigris-500" />
                  <div className="text-[11px] font-mono">Currently linked to <b>{linkedActivity}</b> · {tagDoc.linkedMacroActivityStart} → {tagDoc.linkedMacroActivityFinish}</div>
                </div>
              )}
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={activityQ} onChange={e => setActivityQ(e.target.value)} placeholder="Search WBS / activity name…" className="pl-8" data-testid="swm-cognara-activity-search" />
              </div>
              <div className="rounded border overflow-hidden max-h-[45vh] overflow-y-auto" style={{ borderColor: 'var(--border-color)' }}>
                {filteredActivities.length === 0 && (
                  <div className="p-4 text-center text-[11px] italic" style={{ color: 'var(--text-muted)' }}>
                    {activities.length === 0 ? 'No macro schedule linked yet — set it up in Material Tracking → Linked Resources.' : 'No matching activities.'}
                  </div>
                )}
                {filteredActivities.map(a => {
                  const active = pickedActivity?.id === a.id;
                  const canPick = !a.isSummary && !a.isMilestone;
                  return (
                    <button key={a.id} onClick={() => canPick && setPickedActivity(a)} disabled={!canPick}
                      className="w-full text-left px-3 py-1.5 border-b flex items-center gap-2 hover:bg-slate-50/60 disabled:opacity-100 disabled:cursor-default"
                      style={{ borderColor: 'var(--border-color)',
                               background: active ? 'rgba(242,107,31,0.14)' : (a.isSummary ? 'rgba(148,163,184,0.05)' : 'transparent'),
                               paddingLeft: `${12 + (a.level || 0) * 18}px` }}
                      data-testid={`swm-cognara-activity-${a.id}`}>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0"
                        style={{ background: a.isSummary ? 'rgba(20,58,107,0.16)' : 'rgba(20,58,107,0.08)',
                                 color: '#143A6B', fontWeight: a.isSummary ? 700 : 500 }}>{a.wbs}</span>
                      <span className={`text-[11.5px] flex-1 truncate ${a.isSummary ? 'font-semibold uppercase tracking-wide' : ''}`}
                        style={{ color: 'var(--text-primary)' }} title={a.name}>{a.name}</span>
                      {a.isSummary && (
                        <span className="text-[9px] uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>Summary</span>
                      )}
                      <span className="text-[10px] font-mono shrink-0" style={{ color: 'var(--text-muted)' }}>
                        {a.plannedStart ? a.plannedStart.slice(0, 10) : '—'} → {a.plannedFinish ? a.plannedFinish.slice(0, 10) : '—'}
                      </span>
                      {active && <Check className="w-3.5 h-3.5 text-ember-500 shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <Label className="text-eyebrow">Priority override (1-5, blank = auto)</Label>
                  <Input value={priorityOverride} onChange={e => setPriorityOverride(e.target.value.replace(/[^1-5]/g, ''))} className="w-24 mt-1.5" placeholder="auto" data-testid="swm-cognara-priority" />
                </div>
                <Button onClick={doLink} disabled={busy || !pickedActivity} variant="ember" data-testid="swm-cognara-link">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Link2 className="w-3.5 h-3.5"/> Link &amp; continue</>}
                </Button>
              </div>
            </div>
          ) : tab === 'sequence' ? (
            <div className="space-y-3">
              {!linkedActivity && (
                <div className="p-3 rounded ring-1 text-[11px] flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.10)', borderColor: 'rgba(239,68,68,0.4)', color: 'var(--text-primary)' }}>
                  <AlertTriangle className="w-4 h-4 text-red-500" /> Link a macro activity first (Phase 1).
                </div>
              )}
              <div className="text-sm" style={{ color: 'var(--text-primary)' }}>Cognara analyses all {marks.length} marks (weight, section, elevation, macro-activity window) and proposes a per-mark fab + erect sequence.</div>
              {tagDoc?.cognaraSequenceRationale && (
                <div className="p-2.5 rounded ring-1 text-[11px]" style={{ background: 'rgba(242,107,31,0.08)', borderColor: 'rgba(242,107,31,0.4)', color: 'var(--text-primary)' }}>
                  <b>Cognara rationale:</b> {tagDoc.cognaraSequenceRationale}
                </div>
              )}
              <Button onClick={runSequence} disabled={busy || !linkedActivity} variant="ember" data-testid="swm-cognara-run-sequence">
                {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cognara thinking…</> : <><Sparkles className="w-3.5 h-3.5" /> {suggestedCount + approvedCount > 0 ? 'Re-run Cognara' : 'Run Cognara sequencer'}</>}
              </Button>
              {suggestedCount > 0 && <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{suggestedCount} suggested marks awaiting approval — jump to <b>Phase 3</b>.</p>}
            </div>
          ) : tab === 'approve' ? (
            <div className="space-y-3">
              {suggestedCount === 0 && approvedCount === 0 && (
                <div className="text-[11px] italic" style={{ color: 'var(--text-muted)' }}>No proposals yet — run Cognara sequence in Phase 2.</div>
              )}
              {suggestedCount > 0 && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={approveAll} data-testid="swm-cognara-approve-all"><Check className="w-3.5 h-3.5" /> Approve all suggested</Button>
                  <Button size="sm" onClick={submitApprovals} disabled={busy || Object.keys(decisions).length === 0} variant="ember" data-testid="swm-cognara-submit-approvals">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><CalendarCheck className="w-3.5 h-3.5"/> Submit ({Object.keys(decisions).length})</>}
                  </Button>
                  <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{suggestedCount} suggested · {approvedCount} approved</span>
                </div>
              )}
              <div className="rounded border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                <table className="w-full text-[11px]">
                  <thead style={{ background: 'var(--card-bg-alt)' }}>
                    <tr style={{ color: 'var(--text-muted)' }}>
                      <th className="p-1.5 text-left font-medium">#</th>
                      <th className="p-1.5 text-left font-medium">Mark</th>
                      <th className="p-1.5 text-left font-medium">Fab Start</th>
                      <th className="p-1.5 text-left font-medium">Fab Finish</th>
                      <th className="p-1.5 text-left font-medium">Erect Start</th>
                      <th className="p-1.5 text-left font-medium">Erect Finish</th>
                      <th className="p-1.5 text-center font-medium">P</th>
                      <th className="p-1.5 text-left font-medium">Rationale</th>
                      <th className="p-1.5 text-center font-medium">Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marks.filter(m => ['SUGGESTED','APPROVED','REJECTED'].includes(m.cognaraStatus)).map(m => {
                      const d = decisions[m.markNumber];
                      const isApproved = m.cognaraStatus === 'APPROVED';
                      const isRejected = m.cognaraStatus === 'REJECTED';
                      const fs = d?.edits?.plannedFabStart   ?? (isApproved ? m.plannedFabStart   : m.cognaraSuggestedFabStart);
                      const ff = d?.edits?.plannedFabFinish  ?? (isApproved ? m.plannedFabFinish  : m.cognaraSuggestedFabFinish);
                      const es = d?.edits?.plannedErectStart ?? (isApproved ? m.plannedErectStart : m.cognaraSuggestedErectStart);
                      const ef = d?.edits?.plannedErectFinish?? (isApproved ? m.plannedErectFinish: m.cognaraSuggestedErectFinish);
                      return (
                        <tr key={m.markNumber} className="border-b" style={{ borderColor: 'var(--border-color)', background: isRejected ? 'rgba(239,68,68,0.06)' : isApproved ? 'rgba(14,124,107,0.06)' : undefined }}
                          data-testid={`swm-cognara-mark-row-${m.markNumber}`}>
                          <td className="p-1.5 text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{m.cognaraSequenceIdx || '-'}</td>
                          <td className="p-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{m.markNumber}</td>
                          <td className="p-1.5"><input type="date" value={fs || ''} onChange={e => setEdit(m.markNumber, 'plannedFabStart', e.target.value)} disabled={isApproved || isRejected} className="text-[10px] px-1 py-0.5 rounded border bg-transparent" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}/></td>
                          <td className="p-1.5"><input type="date" value={ff || ''} onChange={e => setEdit(m.markNumber, 'plannedFabFinish', e.target.value)} disabled={isApproved || isRejected} className="text-[10px] px-1 py-0.5 rounded border bg-transparent" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}/></td>
                          <td className="p-1.5"><input type="date" value={es || ''} onChange={e => setEdit(m.markNumber, 'plannedErectStart', e.target.value)} disabled={isApproved || isRejected} className="text-[10px] px-1 py-0.5 rounded border bg-transparent" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}/></td>
                          <td className="p-1.5"><input type="date" value={ef || ''} onChange={e => setEdit(m.markNumber, 'plannedErectFinish', e.target.value)} disabled={isApproved || isRejected} className="text-[10px] px-1 py-0.5 rounded border bg-transparent" style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}/></td>
                          <td className="p-1.5 text-center font-mono">{isApproved ? m.priority : m.cognaraSuggestedPriority}</td>
                          <td className="p-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>{m.cognaraRationale}</td>
                          <td className="p-1.5 text-center">
                            {(isApproved || isRejected) ? (
                              <span className="text-[9px] font-mono uppercase" style={{ color: isApproved ? '#0E7C6B' : '#EF4444' }}>{m.cognaraStatus}</span>
                            ) : (
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => setDecision(m.markNumber, 'APPROVE')}
                                  className="w-6 h-6 rounded flex items-center justify-center"
                                  style={{ background: d?.decision === 'APPROVE' ? '#0E7C6B' : 'transparent', color: d?.decision === 'APPROVE' ? '#fff' : '#0E7C6B', border: '1px solid #0E7C6B' }}
                                  title="Approve" data-testid={`swm-cognara-approve-${m.markNumber}`}><Check className="w-3 h-3" /></button>
                                <button onClick={() => setDecision(m.markNumber, 'REJECT')}
                                  className="w-6 h-6 rounded flex items-center justify-center"
                                  style={{ background: d?.decision === 'REJECT' ? '#EF4444' : 'transparent', color: d?.decision === 'REJECT' ? '#fff' : '#EF4444', border: '1px solid #EF4444' }}
                                  title="Reject" data-testid={`swm-cognara-reject-${m.markNumber}`}><XCircle className="w-3 h-3" /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : tab === 'resources' ? (
            <div className="space-y-3">
              {approvedCount === 0 && (
                <div className="p-3 rounded ring-1 text-[11px]" style={{ background: 'rgba(239,68,68,0.10)', borderColor: 'rgba(239,68,68,0.4)', color: 'var(--text-primary)' }}>
                  <AlertTriangle className="w-4 h-4 inline mr-1 text-red-500" /> Approve at least one mark's plan first (Phase 3).
                </div>
              )}
              <Button onClick={runResources} disabled={busy || approvedCount === 0} variant="ember" data-testid="swm-cognara-run-resources">
                {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cognara sizing crew…</> : <><Users className="w-3.5 h-3.5" /> {resources ? 'Re-run resource plan' : 'Recommend manpower + equipment'}</>}
              </Button>
              {resources && (
                <>
                  <div className="p-2.5 rounded ring-1 text-[11px]" style={{ background: 'rgba(242,107,31,0.08)', borderColor: 'rgba(242,107,31,0.4)', color: 'var(--text-primary)' }}>
                    <b>Cognara rationale:</b> {resources.rationale}
                  </div>
                  <div className="rounded border overflow-hidden max-h-[50vh] overflow-y-auto" style={{ borderColor: 'var(--border-color)' }}>
                    <table className="w-full text-[11px]">
                      <thead style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0 }}>
                        <tr style={{ color: 'var(--text-muted)' }}>
                          <th className="p-1.5 text-left font-medium">Date</th>
                          <th className="p-1.5 text-left font-medium">Marks in scope</th>
                          <th className="p-1.5 text-left font-medium"><HardHat className="w-3 h-3 inline mr-1"/> Manpower</th>
                          <th className="p-1.5 text-left font-medium"><Wrench className="w-3 h-3 inline mr-1"/> Equipment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(resources.days || []).map((day, i) => (
                          <tr key={day.date + i} className="border-b" style={{ borderColor: 'var(--border-color)' }} data-testid={`swm-cognara-day-${day.date}`}>
                            <td className="p-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{day.date}</td>
                            <td className="p-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>{(day.marksInScope || []).slice(0, 6).join(', ')}{(day.marksInScope || []).length > 6 ? ` +${day.marksInScope.length - 6}` : ''}</td>
                            <td className="p-1.5">{(day.manpower || []).map((r, j) => <span key={j} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 text-[9.5px] rounded" style={{ background: 'rgba(20,58,107,0.10)', color: '#143A6B' }}>{r.count}× {r.type}</span>)}</td>
                            <td className="p-1.5">{(day.equipment || []).map((r, j) => <span key={j} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 text-[9.5px] rounded" style={{ background: 'rgba(242,107,31,0.10)', color: '#F26B1F' }}>{r.count}× {r.type}</span>)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {resources.status === 'SUGGESTED' && (
                    <Button onClick={approveResources} disabled={busy} variant="default" data-testid="swm-cognara-approve-resources">
                      <Check className="w-3.5 h-3.5" /> Approve resource plan
                    </Button>
                  )}
                  {resources.status === 'APPROVED' && (
                    <span className="text-[11px] font-mono flex items-center gap-1" style={{ color: '#0E7C6B' }}><Check className="w-3.5 h-3.5" /> Resource plan approved</span>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
